import { useState, useEffect } from "react";
import { Image } from "antd";
import { useIsFetching } from "@tanstack/react-query";
import { Chart, registerables } from "chart.js";
import { useGetAuroraForcasts, useGetAlaskaVdo } from "service/aurora";
import { useGetAurroraForcastSouthImages, useGetAurroraForcastNorthImages } from "service/aurora/swpc";
import { getCurrentForecast, TCurrentForcast, regions } from "tools/aurora";
import { dayjs } from "tools/timezone";
import AuroraChart from "./chart";
import AuroraForcastSlide from "component/aurora/ForcastSlide";
import { getThaiMonth, getMp4Url } from "tools/util";

Chart.register(...registerables);
const localTimeZone = "Asia/Bangkok";

const Aurora = () => {
  const isFetching = useIsFetching();
  const [loadError, setLoadError] = useState(false);
  const [currentDate, setCurrentDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [currentRegion, setCurrentRegion] = useState<"ak" | "na" | "eu" | "np" | "sp">("ak");
  const [currentForcast, setCurrentForcast] = useState<TCurrentForcast>({
    kp: 0,
    local_date: "",
    description: "",
    moonphase: {},
    image: "",
    status: "",
  });

  const { data } = useGetAuroraForcasts();
  const northImages = useGetAurroraForcastNorthImages();
  const southImages = useGetAurroraForcastSouthImages();
  const alaskaVdo = useGetAlaskaVdo();

  let alaska_vdo = "";
  if (typeof alaskaVdo?.data === "string") {
    alaska_vdo = getMp4Url(alaskaVdo.data);
  }

  useEffect(() => {
    if (data) {
      const res = getCurrentForecast(data, currentDate, currentRegion);
      setCurrentForcast(res);
    }
  }, [currentDate, currentRegion, data]);

  const currentTime = new Date().toLocaleTimeString();
  const dateTimeString = `${currentDate} ${currentTime}`;
  const newDate = new Date(dateTimeString);

  document.title = "AURORA FORECAST";
  return !isFetching ? (
    <div className="min-h-[100vh] block bg-[url('asset/image/aurora/bg.jpg')] bg-cover bg-fixed p-[25px]" id="aurora">
      <div className="grid grid-cols-2 gap-[20px] text-white">
        <div className="relative rounded-xl px-[50px] py-[40px] overflow-hidden" id="aurora-forecast">
          <div className="relative z-10">
            <div className="flex justify-between items-center" id="aurora-top">
              <div className="text-white text-[34px] font-backman font-normal aurora-heading">AURORA FORECAST</div>
              <div>
                <div className="font-code font-bold text-[20px] text-right tracking-[2px]">
                  {`${dayjs(currentDate).format("DD")} ${getThaiMonth(parseInt(dayjs(currentDate).format("M")))} ${dayjs(currentDate).format("BBBB")}`} {dayjs().format("HH:mm")} น.
                </div>
                <div className="font-helvetica text-[15px] text-right tracking-[1px]">({dayjs.tz(newDate, localTimeZone).utc().format("UTC+0 MMM DD, YYYY HH:mm")})</div>
              </div>
            </div>
            <div className="flex justify-center gap-3 align-middle my-[45px]" id="aurora-kp">
              <div>
                <div className="font-code font-bold text-[28px] leading-4">AURORA ACTIVITY LEVEL : </div>
                <div className={`font-dina font-bold text-[25px] ${currentForcast.kp > 4 ? "text-[#FF2121]" : currentForcast.kp === 4 ? "text-[#F6EB14]" : "text-[#92D050]"}`}>{currentForcast.status}</div>
              </div>
              <div>
                <div className={`font-moonhouse text-[140px] leading-[30px] ${currentForcast.kp > 4 ? "text-[#FF2121]" : currentForcast.kp === 4 ? "text-[#F6EB14]" : "text-[#92D050]"}`}>
                  {currentForcast.kp}
                  <span className="font-moonhouse text-[40px] text-white ml-2" id="kp-unit">
                    KP
                  </span>
                </div>
              </div>
            </div>
            {typeof currentForcast.kp === "number" && (
              <div className="grid grid-cols-[auto_310px] gap-[30px]" id="aurora-image">
                <div>{currentForcast.image && <Image src={`https://www.gi.alaska.edu/modules/custom/aurora-forecast/images/idl_graphics/${currentForcast.image}`} preview={false} />}</div>
                <div>
                  <div className="relative w-[290px] mb-[25px]" id="kp-step">
                    <div className="flex justify-between relative z-10">
                      <div className="text-center">
                        <div className="bg-[#92D050] border-[1px] border-[white] rounded-full w-[20px] h-[20px] m-auto"></div>
                        <div className="font-helvetica text-[18px]">{`Kp < 4`}</div>
                      </div>
                      <div className="text-center">
                        <div className="bg-[#F6EB14] border-[1px] border-[white] rounded-full w-[20px] h-[20px] m-auto"></div>
                        <div className="font-helvetica text-[18px]">{`Kp = 4`}</div>
                      </div>
                      <div className="text-center">
                        <div className="bg-[#FF2121] border-[1px] border-[white] rounded-full w-[20px] h-[20px] m-auto"></div>
                        <div className="font-helvetica text-[18px]">{`Kp > 4`}</div>
                      </div>
                    </div>
                    <div className="bg-white h-[1px] w-[78%] absolute top-[10px] left-[32px]"></div>
                  </div>
                  <div className="font-sans text-[18px] mb-2" id="aurora-effect-text">
                    <b className="font-sans font-helvetica">Forecast :</b> {currentForcast.description}
                    <div className="font-sans text-[18px] mt-3 flex align-middle gap-3">
                      <b className="font-sans font-helvetica">Moon phase :</b> {currentForcast.moonphase.phase}
                      <div className="h-[30px] w-[30px] mt-[-2px] bg-[url(https://www.gi.alaska.edu/modules/custom/aurora-forecast/images/assets/moonphase.png)]" style={{ backgroundPosition: `${currentForcast.moonphase.x}px ${currentForcast.moonphase.y}px` }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <div className="grid grid-cols-5 gap-3 mt-1" id="aurora-gallery">
                {Object.entries(regions).map((v: any, k: number) => {
                  return (
                    <div key={k} onClick={() => setCurrentRegion(v[0])} className="cursor-pointer duration-300 relative overflow-hidden">
                      <Image src={v[1].image} preview={false} width="100%" className="hover:scale-[110%] origin-bottom duration-300" />
                      <div className="font-code font-bold tracking-[1px]">{v[1].name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto overflow-y-hidden relative z-10 mr-[-30px] ml-[-30px] mt-2" id="aurora-chart">
            <div className="h-[200px] w-[882px]">{data && <AuroraChart data={data} setCurrentDate={setCurrentDate} />}</div>
          </div>
          <div className="bg-[#1C1A16] opacity-75 absolute w-full h-full top-0 left-0"></div>
        </div>
        <div className="grid grid-cols-1" id="aurora-30">
          <div className="relative rounded-xl px-[50px] py-[40px] overflow-hidden mb-[20px]">
            <div className="relative z-10">
              <div className="text-white text-[34px] font-backman font-normal mb-3 aurora-heading">AURORA - 30 MINUTE FORECAST</div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <div>{northImages?.data ? <AuroraForcastSlide animationDelay={100} imagePaths={northImages?.data} /> : <Image src="https://services.swpc.noaa.gov/images/animations/ovation/north/latest.jpg" preview={false} />}</div>
                  <div className="font-code font-bold text-[24px] tracking-[2px] aurora-sub-heading">NORTHERN HEMISPHERE</div>
                </div>
                <div>
                  <div>
                    <div>{southImages?.data ? <AuroraForcastSlide animationDelay={100} imagePaths={southImages?.data} /> : <Image src="https://services.swpc.noaa.gov/images/animations/ovation/south/latest.jpg" preview={false} />}</div>
                  </div>
                  <div className="font-code font-bold text-[24px] tracking-[2px] aurora-sub-heading">SOUTHERN HEMISPHERE</div>
                </div>
              </div>
            </div>
            <div className="bg-[#1C1A16] opacity-75 absolute w-full h-full top-0 left-0"></div>
          </div>
          <div className="relative rounded-xl px-[50px] py-[15px] overflow-hidden">
            <div className="relative z-10">
              <div className="grid grid-cols-2 gap-5 items-center">
                <div className="font-backman text-[34px] aurora-heading">
                  Live Cam
                  <br />
                  AURORA IN ALASKA
                </div>
                <div>{loadError ? <img src="https://allsky.gi.alaska.edu/images/initial-last-30.jpg" alt="" /> : <video controls={false} muted loop itemType="video/mp4" src={alaska_vdo} autoPlay onError={() => setLoadError(true)} />}</div>
              </div>
            </div>
            <div className="bg-[#1C1A16] opacity-75 absolute w-full h-full top-0 left-0"></div>
          </div>
        </div>
      </div>
      <div className="text-[#ccc] mt-3 text-[16px] text-right">Credit : Geophysical Institute, NOAA / NWS Space Weather Prediction Center, University of Alaska Fairbanks Geophysical Institute, Space Weather Prediction Center National Oceanic and Atmospheric Administration</div>
    </div>
  ) : (
    <></>
  );
};

export default Aurora;
